import classNames from 'clsx';
import React, { Children, useState } from 'react';
import type { Settings } from 'react-slick';
import Slider from 'react-slick';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { Icon } from '@swf/sports/components';

import {
  TXTWCL_Go_to_the_next_slide,
  TXTWCL_Go_to_the_previous_slide
} from './Carousel.messages.json';
import styles from './Carousel.scss';
import Pagination from './components/Pagination';

interface Props extends Pick<Settings, 'autoplay' | 'autoplaySpeed' | 'children'> {
  sliderRef?: React.RefObject<Slider | null>;
}

interface ArrowProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  label: string;
}

const PrevArrow: React.FC<ArrowProps> = ({ onClick, label }) => (
  <button type="button" className={styles.prevArrow} onClick={onClick}>
    <Icon name="chevron-small-left" size="xl" label={label} />
  </button>
);

const NextArrow: React.FC<ArrowProps> = ({ onClick, label }) => (
  <button type="button" className={styles.nextArrow} onClick={onClick}>
    <Icon name="chevron-small-right" size="xl" label={label} />
  </button>
);

const Carousel: React.FC<Props> = ({ autoplay = false, autoplaySpeed, sliderRef, children }) => {
  const { formatMessage } = useRegulatoryIntl();
  const [currentSlide, setCurrentSlide] = useState(0);

  const childrenCount = Array.isArray(children) ? children.length : 1;

  const settings: Settings = {
    autoplay,
    autoplaySpeed,
    dots: false, // Disable the default dots
    infinite: true,
    nextArrow: <NextArrow label={formatMessage(TXTWCL_Go_to_the_next_slide)} />,
    prevArrow: <PrevArrow label={formatMessage(TXTWCL_Go_to_the_previous_slide)} />,
    beforeChange: (_, next) => setCurrentSlide(next),
    responsive: [{ breakpoint: 930, settings: { arrows: false } }]
  };

  return (
    <div
      className={classNames(styles.carousel, {
        [styles.singleBanner]: Children.count(children) === 1
      })}
    >
      <Slider ref={sliderRef} {...settings}>
        {children}
      </Slider>
      <Pagination
        slidesCount={childrenCount}
        currentSlide={currentSlide}
        goToSlide={(index) => sliderRef?.current?.slickGoTo(index)}
      />
    </div>
  );
};

export { Carousel };
